/** Colour maths for tokens: parsing, alpha compositing and WCAG 2.2 contrast. Browser safe. */

export interface Rgba {
  r: number;
  g: number;
  b: number;
  /** 0..1 */
  a: number;
}

/** Parse #rgb, #rrggbb or #rrggbbaa. Throws on anything else. */
export function parseHex(hex: string): Rgba {
  const h = hex.trim().replace(/^#/, '');
  const full = h.length === 3 ? [...h].map((c) => c + c).join('') : h;
  if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(full)) throw new Error(`Not a hex colour: ${hex}`);
  const n = (i: number) => parseInt(full.slice(i, i + 2), 16);
  return { r: n(0), g: n(2), b: n(4), a: full.length === 8 ? n(6) / 255 : 1 };
}

function hex2(n: number): string {
  return Math.round(Math.max(0, Math.min(255, n)))
    .toString(16)
    .padStart(2, '0');
}

export function toHex(c: Rgba): string {
  return `#${hex2(c.r)}${hex2(c.g)}${hex2(c.b)}${c.a < 1 ? hex2(c.a * 255) : ''}`;
}

/** The colour at a fraction of its alpha: what color-mix(in oklab, c N%, transparent) renders. */
export function withAlpha(hex: string, fraction: number): string {
  const c = parseHex(hex);
  return toHex({ ...c, a: c.a * fraction });
}

/** Paint `top` (which may be translucent) over an opaque `bottom`. */
export function composite(top: string, bottom: string): string {
  const t = parseHex(top);
  const b = parseHex(bottom);
  const mix = (x: number, y: number) => x * t.a + y * (1 - t.a);
  return toHex({ r: mix(t.r, b.r), g: mix(t.g, b.g), b: mix(t.b, b.b), a: 1 });
}

/** WCAG relative luminance of an opaque colour. */
export function luminance(hex: string): number {
  const { r, g, b } = parseHex(hex);
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio, 1..21. A translucent foreground is composited over the background first. */
export function contrastRatio(fg: string, bg: string): number {
  const front = parseHex(fg).a < 1 ? composite(fg, bg) : fg;
  const a = luminance(front);
  const b = luminance(bg);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Body text needs 4.5:1, large text and UI edges 3:1 (WCAG 2.2 AA). */
export const CONTRAST_TEXT = 4.5;
export const CONTRAST_UI = 3;
